import type {ValuationData} from "@reva/shared/valuation-data";

import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {CommissionValueOrder} from "../payload-types/commission-value-order";

/**
 * Beauftragt bei der ValueAG eine Besichtigung bzw. Dokumentenbeschaffung
 * (POST {@link RevaApi.PATH_VALUATIONS}/{id}/valueOrders/commissionValueOrder, multipart form data).
 * Liefert die Bewertung wie ein Laden; bei geladenem Snapshot (`snapshotNumber`) als dieser Snapshot gerahmt.
 */
export class CommissionValueOrderApiInterface extends SimpleApiInterface<ValuationData> {
    constructor(
        private readonly revaId: string,
        private readonly order: CommissionValueOrder,
        private readonly snapshotNumber: number | null = null,
    ) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl(
            [
                RevaApi.PATH_VALUATIONS,
                this.revaId,
                RevaApi.PATH_VALUATION_VALUE_ORDERS,
                RevaApi.PATH_VALUATION_COMMISSION_VALUE_ORDER,
            ],
            {snapshot: this.snapshotNumber},
        );
    }

    protected buildPayload(): object {
        const {additionalContacts, orderOptions, ownerDocument: _o, registerExtract: _r, ...rest} = this.order;
        return {
            ...rest,
            additionalContacts: additionalContacts?.length ? additionalContacts : null,
            orderOptions: orderOptions?.length ? orderOptions : null,
        };
    }

    protected buildFormData(): FormData {
        const formData = new FormData();
        formData.append("order", JSON.stringify(this.buildPayload()));
        if (this.order.ownerDocument) {
            formData.append("attorneyDocument", this.order.ownerDocument, this.order.ownerDocument.name);
        }
        if (this.order.registerExtract) {
            formData.append("registerExtract", this.order.registerExtract, this.order.registerExtract.name);
        }
        return formData;
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchPOSTFormData(url, credentials, this.buildFormData());
    }
}
